'use client'

import { motion } from 'framer-motion'
import Image from 'next/image'
import { GlowCard } from '@/components/feature/ui/glow-card'
import useEmblaCarousel from 'embla-carousel-react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useCallback } from 'react'

const glowColors = ['blue', 'purple', 'green', 'red', 'teal', 'orange'] as const

const avatarMap: { [key: string]: string } = {
  Aki: '/v2/testimonial/Aki.jpg',
  Zoey: '/v2/testimonial/Zoey.jpg',
  Zeng: '/v2/testimonial/zeng.png',
  Valeria: 'https://heyboss.heeyo.ai/v2/avatar4_aHxHihX9.jpg',
  'Jeff Baumgarten': '/v2/testimonial/Jeff.jpg',
  Rajan: '/v2/testimonial/image6.png',
  'John Thomas': '/v2/testimonial/JohnThomas.jpg',
  François: 'https://heyboss.heeyo.ai/v2/avatar3_LBe__DeQ.jpg',
  Layla: '/v2/testimonial/image9.png',
  Timur: '/v2/testimonial/image10.png',
  'Future AI': '/v2/testimonial/FutureAI.jpg',
  mo: '/v2/testimonial/mo.jpg',
  'Sojin Samuel': '/v2/testimonial/Sojin.jpg',
}

const testimonials = [
  {
    name: 'Aki',
    role: 'building software with ai',
    quote:
      "With @heybossAI you can go from idea to deployment.\n\nTheir agents work in parallel with each other.\n\nI enjoyed trialing their feature as I'm building the site for my ai-powered business card, Drop Card.",
  },
  {
    name: 'Future AI',
    role: 'Filmmaker',
    quote:
      "With \n@heybossAI\n you can go from idea to deployment. \n\nTheir agents work in parallel with each other. \n\nI enjoyed trialing their feature as i'm building the site for my ai-powered business card, Drop Card.",
  },
  {
    name: 'Zoey',
    role: 'Creator',
    quote:
      "Hey, just had to share my experience with @HeyBossAI! 🚀 This tool is a game-changer for productivity and creativity. Whether you're brainstorming ideas or need a quick write-up, it delivers top-notch results every time.",
  },
  {
    name: 'Zeng',
    role: 'AI Creative Creator',
    quote:
      'I built a PH alternative in just 15 minutes using BOSSMODE by HeyBoss AI. I simply gave a prompt, and their AI experts handled the rest—watching the AI agents build my site was incredible!',
  },
  {
    name: 'John Thomas',
    role: 'Founder of VendorCall',
    quote:
      'I built this cool app with heybossAI stuck at my gate at the airport waiting on gate assistants to help with wheelchairs. Had a crowd of about 8 watching me build it. Felt like a magician the way people were amazed',
  },
  {
    name: 'mo',
    role: 'vibe growth marketer',
    quote:
      "I made this landing page in \n@heybossAI\n today with absolute 0 code. Thought I'd make it a template and share with everyone. This is my second template so far.",
  },
  {
    name: 'Sojin Samuel',
    role: 'Software Developer',
    quote:
      'Launched my site 3 days ago and I already got a user asking for API access.\n\nThanks \n@heybossAI\n and \n@lovable_dev\n',
  },
  {
    name: 'Valeria',
    role: 'Baker',
    quote:
      'HeyBoss helped me create a stylish website for my small baking business. Super easy!',
  },
  {
    name: 'Jeff Baumgarten',
    role: 'Co-Founder @ GoChiriAI',
    quote:
      "While everyone is over getting upsold and rate limited on Lovable's free weekend, I am building away on HeyBoss AI and having a great time. Lovable is good too, but I just love what @quxiaoyin and the team have built with HeyBoss.",
  },
  {
    name: 'Rajan',
    role: 'Startup Founder',
    quote:
      'I used HeyBoss to build a landing page for my startup—fast, responsive, and looks great.',
  },
].map((item) => ({
  ...item,
  avatar: avatarMap[item.name] || '/v2/testimonial/image1.png',
}))

interface TestimonialsSectionProps {
  title: string
  sub_title: string
}

export function TestimonialsSection({
  title,
  sub_title,
}: TestimonialsSectionProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
  })

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  return (
    <section className="py-12 sm:py-16">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {title}
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
            {sub_title}
          </p>
        </div>
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {testimonials.map((testimonial, index) => {
              const glowColor = glowColors[index % glowColors.length]
              return (
                <div
                  className="flex-shrink-0 w-full md:w-1/3 lg:w-1/4 xl:w-1/5 p-4"
                  key={testimonial.name}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.5 }}
                    viewport={{ once: true }}
                    className="h-full"
                  >
                    <GlowCard
                      customSize={true}
                      className="p-8 h-full"
                      glowColor={glowColor}
                    >
                      <div className="text-center flex flex-col items-center justify-between h-full">
                        <div className="flex-grow flex flex-col items-center">
                          <div className="relative w-20 h-20 rounded-full overflow-hidden mb-6">
                            <Image
                              src={testimonial.avatar}
                              alt={testimonial.name}
                              fill
                              className="object-cover"
                              sizes="80px"
                            />
                          </div>
                          <p className="text-base text-gray-400 italic flex-grow">
                            "{testimonial.quote}"
                          </p>
                        </div>
                        <div className="mt-6">
                          <h3 className="text-xl font-semibold text-white">
                            {testimonial.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {testimonial.role}
                          </p>
                        </div>
                      </div>
                    </GlowCard>
                  </motion.div>
                </div>
              )
            })}
          </div>
        </div>
        <div className="flex justify-center gap-4 mt-8">
          <button
            type="button"
            className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
            onClick={scrollPrev}
            aria-label="Previous testimonial"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
            onClick={scrollNext}
            aria-label="Next testimonial"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>
      </div>
    </section>
  )
}
